import { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { api, errMsg } from '../api.js';
import { toast } from '../components/Toasts.jsx';
import { TableSkeleton, StatGridSkeleton } from '../components/Skeleton.jsx';
import CoverLetterModal from '../components/CoverLetterModal.jsx';
import TailoredCvModal from '../components/TailoredCvModal.jsx';
import ManualJobModal from '../components/ManualJobModal.jsx';
import ImportJobsModal from '../components/ImportJobsModal.jsx';
import DismissReasonModal, { PRESETS as DISMISS_PRESETS } from '../components/DismissReasonModal.jsx';
import {
  openCompanyResearch as openCompanyResearchGlobal,
  openArtifact,
  useRecPatchSubscription,
} from '../util/artifactHost.js';
import JobDrawer from '../components/JobDrawer.jsx';
import ScoreBadge from '../components/ScoreBadge.jsx';
import EmptyState from '../components/EmptyState.jsx';
import DashboardTour from '../components/DashboardTour.jsx';
import { formatClock, formatDateTime, timeAgoFine } from '../util/time.js';
import { announce } from '../util/a11y.jsx';
import { refreshNavCounts } from '../hooks/useNavCounts.js';
import { useScans } from '../hooks/useScans.js';
import { useRecommendationsFeed, writeRecStatus } from '../hooks/useRecommendationsFeed.js';
import { useOnDemandList } from '../hooks/useOnDemandList.js';
import RecInsights from '../components/dashboard/RecInsights.jsx';
import StatusPill from '../components/StatusPill.jsx';
import HuntingPill from '../components/HuntingPill.jsx';
import KebabMenu from '../components/KebabMenu.jsx';
import MorningBriefPanel from '../components/MorningBriefPanel.jsx';
import DashboardTodayCard from '../components/DashboardTodayCard.jsx';
import ScheduledList from '../components/ScheduledList.jsx';
import { normaliseCityName } from '@hunter/shared/city';
import AlertJobs from '../components/AlertJobs.jsx';
import TriagePanel from '../components/TriagePanel.jsx';
import ScanPanel from '../components/ScanPanel.jsx';
import ScanHealthBanner from '../components/ScanHealthBanner.jsx';
import { SegmentedControl } from '../components/ui';
import { useUrlFilters, serializeFilters, parseQuery, sameQuery } from '../hooks/useUrlFilters.js';
import { compareRecs } from '../util/dashboardSort.js';
import { listViews, saveView, deleteView, setDefaultView, getDefaultView } from '../util/savedViews.js';
import SavedViewsBar from '../components/SavedViewsBar.jsx';
import RecActions from '../components/dashboard/RecActions.jsx';
import { useTriageKeyboard } from '../hooks/useTriageKeyboard.js';
import { useDashboardFacets } from '../hooks/useDashboardFacets.js';
import { useFilterActions } from '../hooks/useFilterActions.js';
import LocationCell, { Th, Td } from '../components/dashboard/TableCells.jsx';
import ClosingChip, { formatCountdown } from '../components/dashboard/ClosingChip.jsx';
import StatCard from '../components/dashboard/StatCard.jsx';
import Zone from '../components/dashboard/Zone.jsx';
import SortTh from '../components/dashboard/SortTh.jsx';
import FilterDropdown from '../components/dashboard/FilterDropdown.jsx';
import SortMenu from '../components/dashboard/SortMenu.jsx';
import KeyboardHelpPanel from '../components/dashboard/KeyboardHelpPanel.jsx';
import ArchivedPanel from '../components/dashboard/ArchivedPanel.jsx';
import DismissedPanel from '../components/dashboard/DismissedPanel.jsx';
import { useGlobalOverlayOpen } from '../util/globalOverlays.js';

// T137 — the Dashboard available-jobs filter contract (URL <-> state). Param
// names are short for clean shareable links; defaults are omitted from the URL.
// T220 — move one row between two status buckets in the cached stats.
//
// An optimistic status change updates the row locally; the pills above the
// table read from `/recommendations/stats`, which the local patch cannot touch.
// Re-fetching stats for every keystroke is what the optimistic path exists to
// avoid, so the count is adjusted in place and the authoritative numbers arrive
// with the next real refresh.
//
// Pure and exported for tests: an off-by-one in a counter is invisible until
// it goes negative, and a stats object can legitimately be null before the
// first load.
// T220 — the row for `id` that is actually on screen.
//
// The desktop <tr> and the mobile card are both in the DOM at all times; only
// CSS hides one. `offsetParent` is null for a `display:none` element, which is
// the cheapest reliable way to ask "is this the one the user can see".
function visibleRecElement(id) {
  const desktop = document.getElementById(`rec-${id}`);
  const mobile = document.getElementById(`rec-m-${id}`);
  if (desktop && desktop.offsetParent !== null) return desktop;
  if (mobile && mobile.offsetParent !== null) return mobile;
  return desktop ?? mobile;
}

export { crossedDismissBoundary } from '../hooks/useRecommendationsFeed.js';

export function bumpStatusCount(stats, from, to) {
  if (!stats?.byStatus || from === to) return stats;
  const byStatus = { ...stats.byStatus };
  if (typeof byStatus[from] === 'number') byStatus[from] = Math.max(0, byStatus[from] - 1);
  if (typeof byStatus[to] === 'number') byStatus[to] += 1;
  return { ...stats, byStatus };
}

const DASHBOARD_FILTER_SPEC = {
  searchQ: { param: 'q', kind: 'text', default: '' },
  filterSources: { param: 'src', kind: 'set' },
  filterCities: { param: 'city', kind: 'set' }, // T194
  filterCountries: { param: 'country', kind: 'set' },
  filterScoreBand: { param: 'score', kind: 'enum', default: 'all' },
  filterAi: { param: 'ai', kind: 'enum', default: 'all' },
  // T142 — additional filter dimensions.
  filterRemote: { param: 'remote', kind: 'set' }, // remote / hybrid / onsite
  filterSeniority: { param: 'sen', kind: 'set' }, // junior … principal
  filterCompany: { param: 'co', kind: 'set' }, // dynamically populated
  filterDateBand: { param: 'added', kind: 'enum', default: 'all' }, // all / 7d / 30d / 90d
  // T142 — sort. Kept in the same URL/saved-view contract so a sorted view is
  // shareable. Default (score / desc) is omitted from the URL.
  sortKey: { param: 'sort', kind: 'enum', default: 'score' }, // score|title|company|location|added
  sortDir: { param: 'dir', kind: 'enum', default: 'desc' }, // asc|desc
};


// T290 — module scope. It was rebuilt inside the component on every render, so
// any memo or effect that named it as a dependency was re-running every time.
// A frozen set of statuses is a constant, not state.
const APPLIED_STATUSES = new Set(['applied', 'screening', 'interview', 'offer', 'rejected', 'withdrawn']);

export default function DashboardPage() {
  const { user, refresh: refreshUser } = useAuth();
  const navigate = useNavigate(); // T232 — deep-link out when a focused rec isn't on this page
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false); // T028
  const [showImportModal, setShowImportModal] = useState(false); // T141 — batch import
  // T161 — scan state + the two async scan flows live in useScans now (wired
  // up below, once `refresh` is defined). See hooks/useScans.js.
  const [, setNowTick] = useState(0); // T115 — slow tick re-renders the relative-time stat cards
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  // T270 — the rows + the paging cursor live in one reducer (see the hook for
  // why they cannot be separate pieces of state). Destructured to the names the
  // JSX below already used, so this is a move, not a rewrite.
  const { feed, load: loadFeed, loadMore: loadMoreFeed, mergeRows, patchRec, applyStatus, restoreRows } = useRecommendationsFeed();
  const { recs, loaded: recsLoaded, offset: pageOffset, total: recsTotal, hasMore: recsHasMore } = feed;
  const [recStats, setRecStats] = useState(null);
  // The list is a WINDOW over a much larger set: the API caps a page at 100
  // and the pool is routinely in the hundreds. Before this, the dashboard
  // showed the top 100 by score and everything under the cut-off was
  // unreachable — including manually imported jobs, which are scored like any
  // other and land wherever they land. Their source also vanished from the
  // filter chips, because that list is derived from the loaded rows.
  // Paging cursor, tracked separately from recs.length: rows also arrive from
  // the source-filter fetch below, and counting those as pages consumed would
  // make the next Load-more skip rows.
  const focusRecRef = useRef(null); // set by the mount effect; used by the hash handler
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionPending, setActionPending] = useState(null); // recId currently being patched
  const [explainPending, setExplainPending] = useState(null); // recId currently being AI-explained
  const [expandedIds, setExpandedIds] = useState(() => new Set()); // T042 — recId set
  const toggleExpand = useCallback((id) => {
    setExpandedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // T048 — search + filter state. Each filter is a Set of allowed values;
  // empty Set means "no filter on this dimension".
  // T137 — filter state lives in the URL (shareable / bookmarkable / Back-able).
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useUrlFilters(DASHBOARD_FILTER_SPEC);
  const {
    searchQ,
    filterSources,
    filterCities,
    filterCountries,
    filterScoreBand,
    filterAi,
    filterRemote,
    filterSeniority,
    filterCompany,
    filterDateBand,
    sortKey,
    sortDir,
  } = filters;
  const clearFilters = () =>
    setFilters({
      searchQ: '',
      filterSources: new Set(),
      filterCities: new Set(),
      filterCountries: new Set(),
      filterScoreBand: 'all',
      filterAi: 'all',
      filterRemote: new Set(),
      filterSeniority: new Set(),
      filterCompany: new Set(),
      filterDateBand: 'all',
      // sort is orthogonal to filters — left untouched by "Clear filters".
    });

  // T142 — clicking a column header sorts by it: toggle direction when it's
  // already the active key, else switch to it with a sensible default dir
  // (score/date default to descending, text columns to ascending).
  const onSortColumn = (key) => {
    if (sortKey === key) {
      setFilters({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      setFilters({ sortKey: key, sortDir: key === 'score' || key === 'added' ? 'desc' : 'asc' });
    }
  };

  // T137 — named saved views (localStorage). activeViewId = the view whose saved
  // query matches the current URL filters.
  const [views, setViews] = useState(() => listViews('dashboard'));
  const currentQuery = useMemo(() => serializeFilters(DASHBOARD_FILTER_SPEC, filters), [filters]);
  const activeViewId = views.find((v) => sameQuery(v.query, currentQuery))?.id ?? null;
  const refreshViews = () => setViews(listViews('dashboard'));
  const onSaveView = (name) => {
    saveView('dashboard', name, currentQuery);
    refreshViews();
  };
  const onApplyView = (v) => setFilters(parseQuery(DASHBOARD_FILTER_SPEC, v.query));
  const onDeleteView = (id) => {
    deleteView('dashboard', id);
    refreshViews();
  };
  const onSetDefaultView = (id) => {
    setDefaultView('dashboard', id);
    refreshViews();
  };

  // Cold-load only: if the URL has no filter params, auto-apply the default view
  // (replace:true so it doesn't create a Back-button trap). A shared link with
  // params always wins.
  useEffect(() => {
    if ([...searchParams].length > 0) return;
    const def = getDefaultView('dashboard');
    if (def) setFilters(parseQuery(DASHBOARD_FILTER_SPEC, def.query), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    setStatsErr(null);
    try {
      // limit=100 is the API's per-page maximum, so this is the FIRST page,
      // not the whole list. T182 added `total`/`hasMore` for exactly this;
      // they drive the Load-more footer below.
      const [statsBody, , recStatsBody] = await Promise.all([
        api.get('/jobs/stats'),
        loadFeed(),
        api.get('/recommendations/stats'),
      ]);
      setStats(statsBody);
      setRecStats(recStatsBody);
      // T079 — keep sidebar counts in sync with what we just fetched.
      refreshNavCounts();
    } catch (e) {
      const msg = errMsg(e, 'Could not load the dashboard');
      setStatsErr(msg);
      toast(msg, { kind: 'error' });
    }
  }, [loadFeed]);

  // Append the next page. The offset is how many rows we already hold rather
  // than a page counter, so it stays correct even if a refresh returned a
  // different-sized first page. De-duped by id: a row can move between pages
  // when a scan re-scores it mid-session, and the sort key is the score.
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      await loadMoreFeed(pageOffset);
    } catch (e) {
      toast(errMsg(e, 'Could not load more recommendations'), { kind: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }, [pageOffset, loadMoreFeed]);

  // T161 — scan flows extracted to a hook. `refresh` is injected so a finished
  // scan can pull fresh stats/recs. Returns the same state the JSX rendered
  // inline before, so the buttons + <ScanPanel> consume it unchanged.
  const { scanning, imapScanning, scanResult, scanProgress, triggerScan, triggerImapScan } = useScans({ refresh });

  // Split into the two tables the dashboard renders. Applied rows are
  // sorted by most-recent-touch (the candidate cares about their pipeline
  // recency, not the historical score). Available rows stay score-sorted.
  // T051 — Applied table now includes all post-applied sub-states. The
  // status dropdown on each row lets the operator track screening →
  // interview → offer / rejected / withdrawn.

  // T421 — a count, not a list.
  //
  // This was a filter AND a date sort on every render, feeding exactly one
  // reader: `appliedRecs.length`, the fallback for `poolApplied` before
  // `recStats` arrives. The UX inventory called it dead wood from a deleted
  // table; it is not dead, it is just doing a sort whose result nothing has
  // ever looked at.
  const appliedFallbackCount = recs.filter((r) => APPLIED_STATUSES.has(r.status)).length;
  // T290 — memoised. A bare `.filter()` is a new array on EVERY render, which
  // made `availableRecs` a new identity every render too, which made every
  // dependency array containing it a claim about identity that was false. The
  // 30-second `nowTick` below re-renders unconditionally, so effects keyed on it
  // fired twice a minute forever — see the scroll effect.
  const availableUnfiltered = useMemo(
    () => recs.filter((r) => !APPLIED_STATUSES.has(r.status) && r.status !== 'dismissed'),
    [recs],
  );

  // T182 follow-up — the two POOL totals come from the server facet, never from
  // `recs`.
  //
  // `recs` is one page of 100. Deriving a total from it made the header say
  // "showing 44 of 44 · 196 new" — two totals for the same set, four words
  // apart, while the footer under the same table said "Showing 100 of 266".
  // Three numbers, one list, one screen. The banner pointing at the tracker was
  // the same mistake: it said 56 applications where the tracker holds 70.
  //
  // recStats.byStatus is already fetched, already scoped to archivedAt: null,
  // and already counts the whole pool.
  const poolAvailable = recStats
    ? (recStats.byStatus?.new ?? 0) + (recStats.byStatus?.shortlisted ?? 0)
    : availableUnfiltered.length;
  // T454 — how much of the pool is where he actually applies.
  //
  // Measured on 241 rows he saw and decided: in Copenhagen he applies to
  // 72% of them, outside 15%. Controlling for location, the SCORE separates
  // nothing — 71% vs 72% inside, 17% vs 15% outside — so the ordering the
  // queue is sorted by is not the thing that decides his attention.
  //
  // The queue holds 928 open rows and 172 of them are in his target
  // locations; sorting by score puts 40 rows at the top of which 14 are.
  // This does not change the sort or the score, which are his to decide: it
  // puts the number on screen and the filter one click away.
  //
  // Counted from `recStats.cities`, which is a POOL facet — the loaded
  // window would give a number smaller than the filter it offers, which is
  // the badge-versus-list defect this codebase keeps finding.
  const targetCityKeys = useMemo(
    () =>
      new Set(
        (user?.preferences?.targetLocations ?? [])
          .map((t) => normaliseCityName(t))
          .filter((c) => c && !/^remote/i.test(c)),
      ),
    [user],
  );
  const poolInTargetCities = useMemo(() => {
    if (!recStats?.cities || targetCityKeys.size === 0) return 0;
    return recStats.cities
      .filter((c) => targetCityKeys.has(c.city))
      .reduce((n, c) => n + (c.count ?? 0), 0);
  }, [recStats, targetCityKeys]);

  const poolApplied = recStats
    ? [...APPLIED_STATUSES].reduce((n, st) => n + (recStats.byStatus?.[st] ?? 0), 0)
    : appliedFallbackCount;

  // T081 — Dismissed recs are hidden by default (the API excludes them).
  // The operator can toggle a Dismissed panel; we fetch on demand and keep
  // them in a separate piece of state to avoid contaminating the main
  // available/applied lists.
  const {
    open: showDismissed,
    rows: dismissedRecs,
    loaded: dismissedLoaded,
    toggle: toggleDismissed,
    reload: loadDismissed,
    loadMore: loadMoreDismissed,
    hasMore: dismissedHasMore,
    total: dismissedTotal,
    loadingMore: dismissedLoadingMore,
  } = useOnDemandList('/recommendations?status=dismissed&limit=100', {
    errorLabel: 'Could not load dismissed recommendations',
  });

  // T220 — the relevance shelf, same shape as the Dismissed panel above and for
  // the same reason: fetched on demand, kept out of `recs` so it cannot leak
  // into the available list or the facet counts.
  //
  // Hiding rows without saying how many is how a filter turns into a lie, so
  // the toggle is always visible when the shelf is non-empty and it carries the
  // count. Archived rows are `status: 'new'` with an `archivedAt` date — the
  // shelf deliberately is not a status (see the Recommendation model).
  const {
    open: showArchived,
    rows: archivedRecs,
    loaded: archivedLoaded,
    toggle: toggleArchived,
    reload: loadArchived,
    loadMore: loadMoreArchived,
    hasMore: archivedHasMore,
    total: archivedTotal,
    loadingMore: archivedLoadingMore,
  } = useOnDemandList('/recommendations?archived=only&limit=100', {
    errorLabel: 'Could not load archived recommendations',
  });
  const restoreArchived = useCallback(
    async (ids) => {
      try {
        await api.patch('/recommendations/archive', { ids, archived: false });
        // A full refresh is right HERE and nowhere else on this page: "Restore
        // all N shown" puts up to a hundred rows back into a pool we do not
        // hold, at score positions we cannot know without asking. The operator
        // has just asked for a large deliberate change to the list.
        await refresh();
        await loadArchived();
        toast(ids.length === 1 ? 'Restored' : `Restored ${ids.length}`, { kind: 'success' });
      } catch (e) {
        toast(e?.body?.message || e?.message || 'Restore failed', { kind: 'error' });
      }
    },
    [refresh, loadArchived],
  );

  // T053 — "Show similar" pivot. Filter Available recs to those that share
  // ≥3 skills with the source rec. Declared here so the useMemo below can
  // read it without TDZ issues.
  const [similarTo, setSimilarTo] = useState(null);
  // T213 — semantic neighbours from the server, replacing the ≥3-shared-skills
  // filter this used to run over the loaded page. That filter could only match
  // rows already on screen, and 71% of jobs have fewer than 3 extracted skills
  // — so for 7 in 10 sources it returned nothing at all, which read as "there
  // is nothing similar" rather than "this filter cannot answer".
  const [similarRecs, setSimilarRecs] = useState(null);
  const [similarState, setSimilarState] = useState('idle'); // idle | loading | ready | error
  const [similarReason, setSimilarReason] = useState(null);
  const showSimilarTo = useCallback(async (rec) => {
    setSimilarTo(rec);
    setSimilarRecs(null);
    setSimilarReason(null);
    setSimilarState('loading');
    try {
      const out = await api.get(`/recommendations/${rec.id}/similar`);
      setSimilarRecs(out.recommendations ?? []);
      setSimilarReason(out.reason ?? null);
      setSimilarState('ready');
    } catch {
      setSimilarState('error');
    }
  }, []);
  const clearSimilar = useCallback(() => {
    setSimilarTo(null);
    setSimilarRecs(null);
    setSimilarReason(null);
    setSimilarState('idle');
  }, []);

  // T025 — Cover letter modal. Open with a specific recommendation; the
  // modal handles all backend calls itself (generate/edit/discard) and
  // calls onUpdated when the rec changes so the dashboard refreshes.
  const [coverLetterRec, setCoverLetterRec] = useState(null);
  const openCoverLetter = useCallback((rec) => setCoverLetterRec(rec), []);
  const closeCoverLetter = useCallback(() => setCoverLetterRec(null), []);
  // T067 — tailored CV modal (per-rec rewrite of Professional Profile +
  // PDF download). Mounted at the page level alongside the cover letter
  // modal so any rec action button can open it.
  const [tailoredCvRec, setTailoredCvRec] = useState(null);
  const openTailoredCv = useCallback((rec) => setTailoredCvRec(rec), []);
  const closeTailoredCv = useCallback(() => setTailoredCvRec(null), []);
  // T070 — dismiss-with-reason. `dismissRequest` opens the modal; null
  // means closed. Shape: { kind: 'single' | 'bulk', recIds: string[],
  // displayTitle?: string }. The modal calls back with the chosen
  // reason (or empty for "no reason"); we then run the actual dismissal.
  const [dismissRequest, setDismissRequest] = useState(null);

  // T130 — job detail drawer + the two AI modals it needs (company research +
  // interview prep were Applications-only; the drawer surfaces all four AI
  // generations from the dashboard). The drawer derives its rec from the live
  // active row (availableRecs[activeRecIndex]) so j/k keeps walking the list
  // while it's open; `drawerOpen` just toggles visibility.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // T189 — merge a PATCHed rec into local state instead of calling refresh().
  // The drawer reads from `recs`, so a full refetch would work but costs three
  // requests and a visible flicker for a one-field edit.
  // T422-0 — an artefact lands on ONE row; refetching the whole feed is the
  // thing T220 removed.
  //
  // The three artefact modals were wired `onUpdated={() => refresh()}`,
  // which is three requests (~1.1s measured) and, worse, exactly the
  // behaviour the comment above `updateRecStatus` warns about: page one
  // replaces `recs`, so every page pulled in with "Load more" vanishes and
  // the extra rows the source/city effect fetched are thrown away. Every
  // other write on this page has been optimistic since T220; these were the
  // last full-refresh call sites, and the adversarial review of the drawer
  // plan (T422) found them by asking which host's contract a shared drawer
  // would have to adopt.
  // T422b — a patch from the shell is applied by whichever page is mounted.
  // Declared after `applyRecPatch` exists, below.
  const applyRecPatch = useCallback(
    (id, patch) => {
      if (!id || !patch) return;
      // T473 — `feed.recs`, which is the property the hook actually exposes.
      //
      // This read `feed.rows`, and the feed's state is
      // `{recs, loaded, offset, total, hasMore}` (useRecommendationsFeed.js:36)
      // — so `feed.rows` was undefined and this threw `Cannot read properties
      // of undefined (reading 'find')` on EVERY artefact generated from the
      // dashboard. Not a drift between contracts; three of three writes dead.
      const row = feed.recs.find((r) => r.id === id);
      if (row) patchRec({ ...row, ...patch });
      // T474 — and the SECOND cache, which the design review caught me
      // missing when I read this file by hand.
      //
      // `similarRecs` is page-local state filled straight from
      // `GET /recommendations/:id/similar`, and the drawer renders from it.
      // So generating a cover letter while looking at a similar row updated
      // the board behind the drawer and not the row in front of him — the
      // same "a tool reporting that its own output does not exist" that
      // T422-0 fixed for `selectedRec` on the other page.
      setSimilarRecs((list) => (list ? list.map((r) => (r.id === id ? { ...r, ...patch } : r)) : list));
    },
    [feed.recs, patchRec],
  );

  useRecPatchSubscription(applyRecPatch);

  const patchRecInPlace = useCallback((updated) => {
    if (!updated?.id) return;
    patchRec(updated);
  }, [patchRec]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // T422a — the modal is mounted once in the shell now; this page only says
  // which company. `childModalOpen` keeps seeing it because every modal
  // announces itself on the global channel (T476).
  const openCompanyResearch = useCallback((rec) => openCompanyResearchGlobal(rec), []);
  // T422b — the modal lives in the shell; this page only names the row, and
  // applies whatever comes back through the patch bus below.
  const openInterviewPrep = useCallback((rec) => openArtifact('interview-prep', rec), []);

  // T048 — apply search + filters to Available recs only. Applied stays
  // unfiltered (it's a smaller list and the operator wants to see all).
  const availableRecs = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    const filtered = availableUnfiltered.filter((r) => {
      // Text search across title, company, skill list, LLM bullet text
      if (q) {
        const hay = [
          r.job?.title,
          r.job?.company,
          r.job?.location?.city,
          r.job?.location?.country,
          ...(r.job?.extracted?.skills ?? []),
          ...(r.reasons ?? []),
          ...(r.llmExplanation?.whyMatch ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterSources.size > 0 && !filterSources.has(r.job?.source)) return false;
      // T194 — matches the canonical city, not what the source wrote. The raw
      // field splits one place across spellings, so filtering on "København"
      // would miss the 439 rows that say "Copenhagen".
      if (filterCities.size > 0 && !filterCities.has(r.job?.cityNormalized)) return false;
      if (filterCountries.size > 0) {
        const c = r.job?.location?.country;
        if (!c || !filterCountries.has(c)) return false;
      }
      if (filterScoreBand !== 'all') {
        const s = r.scoreTotal ?? 0;
        if (filterScoreBand === 'high' && s < 60) return false;
        if (filterScoreBand === 'mid' && (s < 30 || s >= 60)) return false;
        if (filterScoreBand === 'low' && s >= 30) return false;
      }
      if (filterAi === 'yes' && !r.llmExplanation) return false;
      if (filterAi === 'no' && r.llmExplanation) return false;
      // T142 — remote / seniority / company / date-added filters.
      if (filterRemote.size > 0) {
        const rm = r.job?.location?.remote;
        if (!rm || !filterRemote.has(rm)) return false;
      }
      if (filterSeniority.size > 0) {
        const sen = r.job?.extracted?.seniority;
        if (!sen || !filterSeniority.has(sen)) return false;
      }
      if (filterCompany.size > 0 && !filterCompany.has(r.job?.company)) return false;
      if (filterDateBand !== 'all') {
        const days = filterDateBand === '7d' ? 7 : filterDateBand === '30d' ? 30 : 90;
        const cutoff = Date.now() - days * 86_400_000;
        if (!r.createdAt || new Date(r.createdAt).getTime() < cutoff) return false;
      }
      return true;
    });
    // T213 — a similar-pivot replaces the list rather than narrowing it: the
    // neighbours come from the whole embedded corpus, most of which is not on
    // the loaded page. Ordered by similarity, which is what was asked for —
    // "more like this one", not "the best ones".
    if (similarTo) {
      return (similarRecs ?? []).filter((r) => r.id !== similarTo.id);
    }
    // T142 — sort the filtered set by the operator's chosen column + direction.
    return [...filtered].sort((a, b) => compareRecs(a, b, sortKey, sortDir));
  }, [
    availableUnfiltered,
    searchQ,
    filterSources,
    filterCities,
    filterCountries,
    filterScoreBand,
    filterAi,
    filterRemote,
    filterSeniority,
    filterCompany,
    filterDateBand,
    sortKey,
    sortDir,
    similarTo,
    similarRecs,
  ]);

  // T270(2)/T161 — the eight dropdown option lists live in
  // hooks/useDashboardFacets.js. Pure derivations; the server-facet vs
  // loaded-window distinction is documented there.
  // T270(5)/T161 — stable identities, so the hundred-row table can memoise.
  const filterActions = useFilterActions(filters, setFilters);

  const {
    cityCounts,
    availableCities,
    sourceCounts,
    availableSources,
    availableCountries,
    availableRemotes,
    availableSeniorities,
    availableCompanies,
  } = useDashboardFacets({ availableUnfiltered, recStats, scanStats: stats });


  // Picking one of those sources has to reach rows the loaded window does not
  // hold, so fetch them server-side and merge. Keyed on a string rather than
  // the Set itself.
  //
  // T161 — the reason given here used to be "useUrlFilters rebuilds the Set on
  // every render". Measured, it does not: `value` is memoised on `[params]`, so
  // across three renders that leave the URL alone there is exactly one Set
  // identity. The string keys are therefore belt-and-braces, not load-bearing —
  // worth knowing before anyone else designs around the same wrong premise, as
  // this did.
  const sourcesKey = [...filterSources].sort().join(',');
  const citiesKey = [...filterCities].sort().join(',');
  useEffect(() => {
    if (!sourcesKey && !citiesKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const qs = [
          sourcesKey ? `sources=${encodeURIComponent(sourcesKey)}` : null,
          citiesKey ? `cities=${encodeURIComponent(citiesKey)}` : null,
        ].filter(Boolean).join('&');
        const body = await api.get(`/recommendations?limit=100&${qs}`);
        if (cancelled) return;
        mergeRows(body.recommendations ?? []);
      } catch {
        // Non-fatal: the client-side filter still matches whatever is loaded.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourcesKey, citiesKey, mergeRows]);

  const activeFilterCount =
    (searchQ ? 1 : 0) +
    filterSources.size +
    filterCities.size +
    filterCountries.size +
    (filterScoreBand !== 'all' ? 1 : 0) +
    (filterAi !== 'all' ? 1 : 0) +
    filterRemote.size +
    filterSeniority.size +
    filterCompany.size +
    (filterDateBand !== 'all' ? 1 : 0);

  // T085 — Keyboard navigation. activeRecIndex points into the
  // *currently-displayed* availableRecs array. Set to -1 when nothing's
  // active (initial state / list empty / focus left the dashboard).
  const [activeRecIndex, setActiveRecIndex] = useState(-1);
  const [showKbHelp, setShowKbHelp] = useState(false);
  const [tourReplay, setTourReplay] = useState(false); // T139 — manual "Take the tour" replay

  // T130 — the drawer always shows the live active rec (so artifact freshness
  // reflects the latest refresh()); opening from a row syncs the cursor to it.
  const drawerRec = drawerOpen ? (availableRecs[activeRecIndex] ?? null) : null;
  const openDrawer = useCallback(
    (rec) => {
      setActiveRecIndex(availableRecs.findIndex((r) => r.id === rec.id));
      setDrawerOpen(true);
    },
    [availableRecs],
  );
  // While an AI modal (z-50) is open over the drawer (z-40), the drawer's
  // Escape must yield to the modal — pass this so it early-returns.
  const childModalOpen = !!(coverLetterRec || tailoredCvRec);
  // T290b — anything the operator can open ON TOP of the drawer belongs here.
  // The first pass added only the dismiss dialog and left three more: the manual
  // and import modals — the manual one is reachable from the palette while a job
  // is open, which is how this was found; the import one is not, and is here
  // because "an overlay is over the drawer" is the rule, not "the palette can
  // open it" — and
  // the keyboard-help sheet. The triage-shortcut guard 350 lines below already
  // listed all of them, so the two gates disagreed about what counts as "an
  // overlay is open" — and this is the one that decides whether Escape takes the
  // operator's place in the queue with it.
  //
  // The palette is not page state at all: App.jsx mounts it outside <Routes>,
  // so it reports itself through util/globalOverlays.js.
  //
  // T290 — the dismiss dialog belongs in that list and was missing from it.
  // Both overlays listen on the window and neither stops propagation, so this
  // prop is the ONLY coordination between them: open a job, click Dismiss,
  // change your mind, press Escape — and the single keypress cancelled the
  // dismiss AND closed the drawer, losing the operator's place in the queue for
  // the one action that was supposed to be reversible. Escape now cancels the
  // dialog and leaves the job open behind it.
  const globalOverlayOpen = useGlobalOverlayOpen();
  const overlayOverDrawer =
    childModalOpen ||
    !!dismissRequest ||
    showManualModal ||
    showImportModal ||
    showKbHelp ||
    globalOverlayOpen;

  // T049 — bulk selection. Set of rec IDs the operator has checkboxed.
  // Sticky bar at top of Available section when any are selected.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = (id) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(new Set(availableRecs.map((r) => r.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Apply a status change to all selected. Client-side loop (no bulk
  // endpoint yet) — fine for ≤20 rows; if the operator hits 50+ regularly
  // we add POST /api/recommendations/bulk later.
  // T070 — bulk dismiss routes through the reason modal so the operator
  // gets prompted ONCE, and the same reason is applied to every selected
  // rec. Other bulk statuses (shortlist, applied) keep the direct path.
  const bulkUpdateStatus = async (status) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (status === 'dismissed') {
      requestBulkDismiss(ids);
      return;
    }
    let ok = 0;
    let failed = 0;
    // T253 — same two problems as bulkExplain: no pending state while N PATCHes
    // run, then a full refresh that discards every loaded page.
    setActionPending('bulk');
    // The real previous status per row, captured before the loop. Hardcoding
    // 'new' here decremented a count the row had not been in: bulk-applying a
    // selection that included already-shortlisted rows walked the "new" chip
    // down and left "shortlisted" too high.
    const previous = new Map(ids.map((id) => [id, recs.find((r) => r.id === id)?.status ?? 'new']));
    try {
      for (const id of ids) {
        try {
          await api.patch(`/recommendations/${id}`, { status });
          const from = previous.get(id);
          // T270 — one call: the row and the cursor cannot move apart.
          applyStatus(id, from, status, { status });
          // Was covered by the refresh() this replaced: without it the
          // "N new · M shortlisted" chips above the table go stale after a bulk move.
          setRecStats((st) => bumpStatusCount(st, from, status));
          ok++;
        } catch {
          failed++;
        }
      }
    } finally {
      setActionPending(null);
    }
    refreshNavCounts();
    clearSelection();
    if (failed > 0) {
      toast(`${ok} updated, ${failed} failed`, { kind: 'error' });
    } else {
      const verb = status === 'dismissed' ? 'Dismissed' : status === 'shortlisted' ? 'Shortlisted' : 'Updated';
      toast(`${verb} ${ok}`, { kind: 'success' });
    }
  };
  const bulkExplain = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    let ok = 0;
    let failed = 0;
    // T253 — N sequential LLM calls, seconds each, previously with no signal at
    // all: the bulk bar stayed enabled and the first feedback was the summary
    // toast. At 20 selected that is half a minute of a UI that looks idle.
    setActionPending('bulk');
    toast(`Explaining ${ids.length}…`, { kind: 'info' });
    try {
      for (const id of ids) {
        try {
          const body = await api.post(`/recommendations/${id}/explain`);
          // Patch as each lands, so the list fills in progressively instead of
          // staying frozen until the whole batch finishes.
          if (body?.recommendation) patchRecInPlace(body.recommendation);
          ok++;
        } catch {
          failed++;
        }
      }
    } finally {
      setActionPending(null);
    }
    clearSelection();
    if (failed > 0) {
      toast(`Explained ${ok}, ${failed} failed`, { kind: failed === ids.length ? 'error' : 'info' });
    } else {
      toast(`Explained ${ok}`, { kind: 'success' });
    }
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  // T074 — Command palette dispatches CustomEvents so this page can react
  // without an explicit prop wiring. "open-manual-job" opens the same
  // ManualJobModal as the in-page button; "focus-rec" expands + scrolls
  // a recommendation row into view.
  useEffect(() => {
    const onOpenManual = () => setShowManualModal(true);
    const onOpenImport = () => setShowImportModal(true); // T141
    const focusRec = (id) => {
      if (!id) return;
      setExpandedIds((cur) => {
        const next = new Set(cur);
        next.add(id);
        return next;
      });
      // Wait one frame for the row to render in expanded form.
      requestAnimationFrame(() => {
        const el = visibleRecElement(id);
        // T232 — never fail silently. The row can be absent legitimately: the
        // rec may sit behind an active filter, or have moved into the tracker
        // (which this page does not render). Saying so beats a dead click.
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          toast('That job is not in the current view — it may be filtered out or already in your tracker.', {
            kind: 'info',
            action: { label: 'Open tracker', onClick: () => navigate('/applications') },
          });
        }
      });
    };
    window.addEventListener('hunter:open-manual-job', onOpenManual);
    window.addEventListener('hunter:open-import-jobs', onOpenImport);
    const onFocusRec = (e) => focusRec(e?.detail?.id);
    focusRecRef.current = focusRec;
    window.addEventListener('hunter:focus-rec', onFocusRec);
    return () => {
      window.removeEventListener('hunter:open-manual-job', onOpenManual);
      window.removeEventListener('hunter:open-import-jobs', onOpenImport);
      window.removeEventListener('hunter:focus-rec', onFocusRec);
    };
    // `navigate` is stable across renders in react-router v6, and the feed's
    // writers are useCallback([]) over a dispatch. Declared rather than
    // suppressed: an empty array here is a claim about identities that the next
    // person has to go and verify.
  }, [navigate]);

  // The same request, arriving by URL instead of by event.
  //
  // Both the digest and the command palette navigate to `/#rec-<id>` and then
  // dispatch hunter:focus-rec. That works from a page that is already mounted
  // alongside this one, and not at all from another route: the navigation
  // unmounts the sender and mounts this page AFTER the event has been
  // dispatched into nothing. The digest's "open in app" therefore landed on the
  // dashboard and highlighted nothing.
  //
  // The id is already in the URL, so the fix needs no new channel. Waits for
  // the first page of rows, because focusing a row that has not rendered yet
  // would fire the "not in the current view" toast on a rec that is simply
  // still loading.
  //
  // The id is READ during the first render, not in the effect that uses it.
  // Effects run later and other code gets there first: the default-view effect
  // above calls setFilters on mount, and react-router's setSearchParams rebuilds
  // the location from pathname + search — dropping the hash. So on any account
  // with a saved default view the id was gone before this could look at it, and
  // "open in app" silently did nothing again. A useState initialiser runs before
  // every effect, so nothing can strip it first.
  const [pendingFocusId] = useState(() =>
    typeof window === 'undefined' ? null : (window.location.hash.match(/^#rec-(.+)$/)?.[1] ?? null),
  );
  const hashFocusedRef = useRef(false);
  useEffect(() => {
    if (hashFocusedRef.current || !recsLoaded || !pendingFocusId) return;
    hashFocusedRef.current = true;
    // Drop it from the URL so a reload does not re-focus a row the operator has
    // moved on from, and so it does not survive into a shared link.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    focusRecRef.current?.(pendingFocusId);
  }, [recsLoaded, pendingFocusId]);

  // T255 — the j/k cursor was pure styling: a ring on a row, with no
  // aria-activedescendant and nothing announced. For a screen-reader user the
  // primary interaction of the primary page was completely silent — the toasts
  // report what an action DID, never where the cursor IS.
  //
  // Announced rather than focused on purpose: moving real focus would change
  // what Tab does next and fight the existing flow, which works.
  //
  // T290 — keyed on the announcement TEXT, not on the array. `availableRecs` was
  // a fresh identity every render, so this re-announced the cursor row on every
  // keystroke in the search box and twice a minute on the tick. A live region
  // that repeats itself is one a screen-reader user turns off.
  const cursorAnnouncement = (() => {
    const cur = availableRecs[activeRecIndex];
    if (!cur) return null;
    return `Row ${activeRecIndex + 1} of ${availableRecs.length}: ${cur.job?.title ?? 'untitled'}`
      + `${cur.job?.company ? ` at ${cur.job.company}` : ''}, score ${cur.scoreTotal ?? 0}`;
  })();
  useEffect(() => {
    if (cursorAnnouncement) announce(cursorAnnouncement);
  }, [cursorAnnouncement]);


  // Scroll the active row into view when it changes.
  //
  // T290 — keyed on the active row's ID, not on the array. The dependency was
  // `[activeRecIndex, availableRecs]`, and `availableRecs` was a fresh identity
  // on every render, so this fired on every render — including the unconditional
  // 30-second tick. The operator would press `j` a few times, scroll away with
  // the trackpad to read further down the list, and within thirty seconds be
  // smooth-scrolled back to the cursor row. Every thirty seconds, for as long as
  // he stayed on the page.
  //
  // The ID is the thing that should trigger it: "the active row changed".
  const activeRecId = activeRecIndex >= 0 ? availableRecs[activeRecIndex]?.id ?? null : null;
  useEffect(() => {
    if (!activeRecId) return;
    const el = visibleRecElement(activeRecId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeRecId]);

  // Clamp activeRecIndex when the list shrinks (e.g. an action removed
  // the active row from view).
  useEffect(() => {
    if (activeRecIndex >= availableRecs.length) {
      setActiveRecIndex(availableRecs.length === 0 ? -1 : availableRecs.length - 1);
    }
  }, [availableRecs.length, activeRecIndex]);

  // T043 — Status changes surface a toast. Dismiss in particular gets an
  // Undo action that flips status back to whatever it was before.
  // T070 — Optional `reason` arg piggy-backs on the PATCH body as
  // dismissedReason (only meaningful when status === 'dismissed').
  //
  // T220 — optimistic, no full refresh.
  //
  // This used to `await refresh()` after every single status change, which
  // re-fetched page one and REPLACED `recs`. Three consequences, all of them
  // felt during triage: every page pulled in with "Load more" vanished on the
  // next keystroke; the extra rows fetched by the source/city effect were
  // thrown away and not re-fetched (its dependency key had not changed); and a
  // one-key decision cost 4 requests instead of 1.
  //
  // The optimistic pattern is not new here — `patchRecInPlace` and
  // ApplicationsPage both already do it. The row is patched locally, the PATCH
  // is sent, and on failure the previous status is put back, so a dropped
  // request cannot leave the screen disagreeing with the database.
  const updateRecStatus = (id, status, opts = {}) =>
    writeRecStatus({
      id,
      status,
      opts,
      feed,
      applyStatus,
      setActionPending,
      setRecStats,
      bumpStatusCount,
      // `currentlyIs` is passed through as `from` so the undo does not have to
      // re-derive it from this closure's now-stale feed.
      onUndo: (recId, restoreTo, currentlyIs) =>
        updateRecStatus(recId, restoreTo, { silent: true, from: currentlyIs }),
    });

  // T070 — Opens the reason modal instead of dismissing immediately.
  // Single-rec path called from per-row action buttons.
  const requestDismiss = (rec) => {
    setDismissRequest({
      kind: 'single',
      recIds: [rec.id],
      displayTitle: rec.job?.title ?? '',
    });
  };

  // Bulk variant — used by the multi-select action bar.
  const requestBulkDismiss = (ids) => {
    if (!ids || ids.length === 0) return;
    setDismissRequest({ kind: 'bulk', recIds: ids });
  };

  // Resolves the modal: applies the reason to every targeted rec.
  const confirmDismissWithReason = async (reason) => {
    if (!dismissRequest) return;
    const { recIds } = dismissRequest;
    setDismissRequest(null); // close modal first so the toast renders cleanly
    if (recIds.length === 1) {
      await updateRecStatus(recIds[0], 'dismissed', { reason });
    } else {
      // Bulk. T253/T270 — this ended in a full refresh(), which is the most
      // damaging place for one: dismissing twenty rows deep in a loaded list
      // then collapsed the list back to page one. Each row is patched locally
      // and the cursor moves once per row, which is exactly what the server's
      // own pool did.
      setActionPending('bulk');
      const previous = new Map(recIds.map((id) => [id, recs.find((r) => r.id === id)?.status ?? 'new']));
      try {
        await Promise.all(
          recIds.map((id) =>
            api.patch(`/recommendations/${id}`, { status: 'dismissed', dismissedReason: reason }),
          ),
        );
        for (const id of recIds) {
          applyStatus(id, previous.get(id), 'dismissed', { status: 'dismissed', dismissedReason: reason });
          setRecStats((st) => bumpStatusCount(st, previous.get(id), 'dismissed'));
        }
        refreshNavCounts();
        clearSelection?.();
        toast(`Dismissed ${recIds.length}`, { kind: 'success' });
      } catch (e) {
        // Partial failure is possible here (N independent PATCHes), and nothing
        // local has been changed yet — so a full reload is the honest recovery.
        await refresh();
        toast(errMsg(e, 'Some dismissals failed'), { kind: 'error' });
      } finally {
        setActionPending(null);
      }
    }
  };

  // T020 — On-demand LLM explanation for a single rec. Surfaces a toast
  // on error (with a helpful nudge when the Anthropic key is missing).
  const explainRec = async (id) => {
    setExplainPending(id);
    try {
      // T253 — was `await refresh()`, which replaces `recs` with page one. The
      // invariant T220 established for updateRecStatus applies identically
      // here: pressing `e` deep in the list collapsed every page pulled in with
      // Load more and threw away the rows the source/city effect had merged in.
      // The endpoint already returns exactly the shape patchRecInPlace wants.
      const body = await api.post(`/recommendations/${id}/explain`);
      if (body?.recommendation) patchRecInPlace(body.recommendation);
      toast('AI explanation ready', { kind: 'success' });
    } catch (e) {
      const msg =
        e.body?.error === 'anthropic_key_missing'
          ? 'Set your Anthropic API key in Settings → Connectors first.'
          : e.body?.message || e.message;
      toast(msg, { kind: 'error' });
    } finally {
      setExplainPending(null);
    }
  };

  // T270(2)/T161 — the map itself lives in hooks/useTriageKeyboard.js.
  // Placed here, below every handler it takes: inside the old inline effect
  // these were read from a closure that ran later, so declaration order did
  // not matter. Passed as arguments they are evaluated at call time, and a
  // hook call above `const updateRecStatus` is a TDZ ReferenceError at mount.
  // T290 — the shortcuts are DISARMED while anything is over the list.
  //
  // The hook ignored inputs and modifier keys but nothing else, so every modal
  // on this page left `d`, `a`, `s` and the 1-6 dismiss presets live over the
  // row behind it. Pressing `d` while the dismiss-reason modal was open
  // dismissed the row underneath with no reason; `1`-`6` fired a preset at it
  // while the operator believed he was choosing one for the row in front of
  // him. The most frequent action in triage was also the easiest to fire by
  // accident at the wrong row.
  //
  // Listed rather than folded into one flag: each of these is a different
  // surface, and a new one added without a line here is a new way to lose a
  // row. `childModalOpen` already gathers the four AI modals.
  // T290c — the first-run tour was missing, and its open state lives inside
  // DashboardTour rather than here, so this list could not have named it. That
  // is the same shape as the command palette, and globalOverlays.js is the
  // channel built for it: while the tour is up, `j`/`d`/`1`-`6` were acting on
  // the queue behind it, and `d` is not undoable without finding the row again.
  const triageBlocked = !!(
    dismissRequest || drawerOpen || showManualModal || showImportModal || showKbHelp
    || childModalOpen || globalOverlayOpen
  );

  useTriageKeyboard({
    enabled: !triageBlocked,
    showKbHelpOpen: showKbHelp,
    availableRecs,
    activeRecIndex,
    setActiveRecIndex,
    updateRecStatus,
    requestDismiss,
    explainRec,
    openCoverLetter,
    openTailoredCv,
    setDrawerOpen,
    setShowKbHelp,
    dismissPresets: DISMISS_PRESETS,
  });

  return (
    <>
      {/* T025 — Cover letter modal. Mounted at the top so it overlays the page. */}
      {coverLetterRec && (
        <CoverLetterModal
          rec={coverLetterRec}
          onClose={closeCoverLetter}
          // T474 — the producer names its own fields; the host just applies
          // the patch. This mapping lived here and on the tracker, spelled
          // differently.
          onUpdated={(patch) => applyRecPatch(coverLetterRec.id, patch)}
        />
      )}
      {tailoredCvRec && (
        <TailoredCvModal
          rec={tailoredCvRec}
          onClose={closeTailoredCv}
          onUpdated={(patch) => applyRecPatch(tailoredCvRec.id, patch)}
        />
      )}
      {showManualModal && (
        <ManualJobModal
          onClose={() => setShowManualModal(false)}
          onCreated={() => refresh()}
        />
      )}
      {showImportModal && (
        <ImportJobsModal
          onClose={() => setShowImportModal(false)}
          onCreated={() => refresh()}
        />
      )}
      {dismissRequest && (
        <DismissReasonModal
          count={dismissRequest.recIds.length}
          jobTitle={dismissRequest.displayTitle}
          onConfirm={confirmDismissWithReason}
          onCancel={() => setDismissRequest(null)}
        />
      )}
      {/* T130 — job detail drawer (z-40). The four AI modals open above it (z-50). */}
      {drawerRec && (
        <JobDrawer
          rec={drawerRec}
          childModalOpen={overlayOverDrawer}
          // T474 — the drawer's panels emit PATCHES now, not whole rows.
          // `patchRecInPlace` took a whole row and guarded on `updated?.id`,
          // so a bare patch would have been dropped in silence — and the
          // Salary panel's patch is the one that could never arrive anyway,
          // because the PATCH response omitted `salaryExpectation` until this
          // ticket added it.
          onRecPatched={(patch) => applyRecPatch(drawerRec.id, patch)}
          onClose={closeDrawer}
          onUpdate={updateRecStatus}
          onExplain={explainRec}
          onRequestDismiss={requestDismiss}
          pending={actionPending}
          explainPending={explainPending}
          onOpenCoverLetter={openCoverLetter}
          onOpenTailoredCv={openTailoredCv}
          onOpenInterviewPrep={openInterviewPrep}
          onOpenCompanyResearch={openCompanyResearch}
        />
      )}
      {/* T256(2) — five buttons in a nowrap row: below ~1100px they crushed the
          <h1> and then overflowed. Wrap them, and move "Take the tour" into the
          "?" panel — it is a once-ever action that was sitting permanently
          beside the daily ones. */}
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-brand-500">Dashboard</h1>
          {/* T417 — the tagline carried a link to the source repository.
              A developer-era artefact sitting in the header of the page he
              opens every morning: it answers a question he has never asked
              and occupies the line that should say what today looks like. */}
          <p className="mt-1 text-sm text-slate-400">Job Search Operating System</p>
        </div>
        {/* T417 — four ways in, and the two he actually uses come first.
            Measured over the last four weeks: 53% of his applications came
            from Import and Add manually, 47% from the scanned queue. The
            header had the two scan buttons on the right in the primary
            style and the two he reaches for most as quiet outlines on the
            left — a layout from when the scan was the whole product.
            Equal weight, his order. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowManualModal(true)}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
            title="Paste a job posting URL or text — Claude extracts the fields and scores it"
          >
            + Add manually
          </button>
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
            title="Import many saved job links at once — paste or upload a document of URLs"
          >
            ⬆ Import links
          </button>
          <button
            type="button"
            onClick={triggerImapScan}
            disabled={imapScanning}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
            // T375 — the label said LinkedIn and the scan had long since stopped
            // being only that: it also reads employer replies, attaches them to
            // applications and raises stage changes to approve. He pressed it
            // after a reply arrived and was told "no new jobs".
            title="Read your inbox: LinkedIn job alerts and employer replies (read-only)"
          >
            {imapScanning ? 'reading inbox…' : '📧 Fetch mail'}
          </button>
          <button
            type="button"
            onClick={triggerScan}
            disabled={scanning}
            aria-busy={scanning}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
            title="Fetch new postings from every enabled source"
          >
            {scanning ? '⟳ scanning…' : '⟳ Scan now'}
          </button>
        </div>
      </header>

      {/* T100 — Scan health: red when the loop is broken (fatal/stale),
          amber when sources are failing/silent. Renders nothing when healthy.
          Page-level alert above the zones; lastScanAt re-triggers the health
          fetch after an in-page manual scan lands. */}
      <ScanHealthBanner lastScanAt={stats?.lastScan?.runAt ?? null} />

      {/* T052 — onboarding checklist; self-hides once setup is complete. */}
      <OnboardingBanner user={user} stats={stats} />

      {/* ── TODAY — is the loop healthy + what's today's picture (T129) ──── */}
      <Zone title="Today" tone="accent">
        {/* T322 — commitments before capacity. The goal stepper says how much
            work to do; this says what is already promised, and a scheduled
            interview outranks a daily target. Self-hides when nothing is
            scheduled. */}
        {/* T415 — the same component Tasks renders, over the same window.
            Two implementations of "what is coming up" meant the same
            interview could be on one screen and not the other (30 days here,
            60 there). Compact: the nearest few, then a link to the rest. */}
        <ScheduledList limit={5} />

        {/* T412 — jobs found inside his LinkedIn alert mails. This sat at the
            top of the Mail page, above the mail itself, although its output
            is an imported job and lands here. It is an import route, so it
            belongs with the other import routes. */}
        <AlertJobs />
        {/* Analytics-Roadmap P1 — daily pace + goal, where the day starts. */}
        <DashboardTodayCard />
        {/* T095 — live scan progress + collapsed result; self-hides when idle. */}
        <ScanPanel
          scanning={scanning}
          scanResult={scanResult}
          knownSources={
            // Pull the last scan's source list for the live cycling UI;
            // gives us real, recognisable adapter names.
            scanResult?.sourcesQueried ?? stats?.lastScan?.sourcesQueried ?? []
          }
          autoExplain={scanResult?.autoExplain}
          progress={scanProgress}
        />
        {/* T087 — Morning brief: 1 paragraph from Claude; self-loads + caches. */}
        <MorningBriefPanel aiKeyReady={!!user?.connectors?.anthropicKey} />
        {/* T083 — Inline hunt-filter editor (target roles + locations). */}
        <HuntingPill user={user} onPrefsUpdated={refreshUser} />
        {/* T249 — a skeleton means "loading". When the first refresh FAILED it
            meant "loading, forever": the only signal was a toast that had long
            since faded, and the page offered no way to try again. */}
        {statsErr && !stats ? (
          <EmptyState
            icon="⚠"
            title="Could not load the dashboard"
            body={statsErr}
            actions={[{ label: 'Retry', onClick: refresh }]}
          />
        ) : !stats ? (
          <StatGridSkeleton count={2} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              title="Next scan"
              value={
                stats?.nextScan
                  ? formatCountdown(
                      stats.nextScan.nextScanAt
                        ? new Date(stats.nextScan.nextScanAt).getTime() - Date.now()
                        : stats.nextScan.msUntilNextScan,
                    )
                  : '—'
              }
              hint={
                stats?.nextScan
                  ? `${stats.nextScan.timezone} · daily at ${String(stats.nextScan.digestHour).padStart(2, '0')}:05`
                  : 'set digestHour in /profile'
              }
            />
            <StatCard
              title="Last scan"
              value={
                stats?.lastScan
                  ? `${formatClock(stats.lastScan.runAt)} · ${timeAgoFine(stats.lastScan.runAt)}`
                  : 'never'
              }
              hint={
                stats?.lastScan
                  ? `${stats.lastScan.totalJobsFound} found · ${stats.lastScan.totalJobsNew} new · ${stats.lastScan.durationMs}ms · ${stats.lastScan.triggeredBy}`
                  : 'click "Scan now"'
              }
            />
          </div>
        )}
      </Zone>

      {/* ── WORK QUEUE — the operator's actual job (T129) ───────────────── */}
      <Zone title="Work queue" tone="muted" hint="Score-ranked jobs you haven't acted on yet.">
        {/* T069 — applied jobs live in /applications now; just a pointer here. */}
        {recsLoaded && poolApplied > 0 && (
          <section className="rounded-lg border border-slate-800 bg-slate-900/30 p-3 text-xs text-slate-400">
            <span>
              <strong className="text-slate-200">{poolApplied}</strong> applied position{poolApplied === 1 ? '' : 's'} are tracked in the{' '}
              <Link to="/applications" className="text-brand-500 underline decoration-dotted hover:text-brand-400">
                Application tracker
              </Link>
              .
            </span>
          </section>
        )}

        {/* T137 — saved-view chips + Save-current-view (above the filter bar). */}
        <SavedViewsBar
          views={views}
          activeViewId={activeViewId}
          hasActiveFilters={activeFilterCount > 0}
          onApply={onApplyView}
          onSave={onSaveView}
          onDelete={onDeleteView}
          onSetDefault={onSetDefaultView}
        />

        {statsErr && !recsLoaded ? (
          <section>
            <h2 className="text-xs uppercase tracking-wide text-slate-500">Available jobs</h2>
            <EmptyState
              icon="⚠"
              title="Could not load your recommendations"
              body={statsErr}
              actions={[{ label: 'Retry', onClick: refresh }]}
            />
          </section>
        ) : !recsLoaded ? (
          <section>
            <h2 className="text-xs uppercase tracking-wide text-slate-500">Available jobs</h2>
            <TableSkeleton rows={5} />
          </section>
        ) : (
          <AvailableJobsTable
            poolInTargetCities={poolInTargetCities}
            targetCityKeys={targetCityKeys}
            onFilterTargetCities={() => setFilters({ filterCities: new Set(targetCityKeys) })}
            recs={availableRecs}
            totalUnfilteredCount={poolAvailable}
            loadedCount={pageOffset}
            totalCount={recsTotal}
            hasMore={recsHasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            pending={actionPending}
            explainPending={explainPending}
            expandedIds={expandedIds}
            onToggleExpand={toggleExpand}
            onUpdate={updateRecStatus}
            onExplain={explainRec}
            newCount={recStats?.byStatus?.new ?? 0}
            shortlistedCount={recStats?.byStatus?.shortlisted ?? 0}
            hasAny={recs.length > 0}
            searchQ={searchQ}
            onSearchChange={filterActions.onSearchChange}
            availableSources={availableSources}
            sourceCounts={sourceCounts}
            availableCities={availableCities}
            cityCounts={cityCounts}
            filterCities={filterCities}
            onToggleCity={filterActions.onToggleCity}
            availableCountries={availableCountries}
            filterSources={filterSources}
            filterCountries={filterCountries}
            filterScoreBand={filterScoreBand}
            filterAi={filterAi}
            onToggleSource={filterActions.onToggleSource}
            onToggleCountry={filterActions.onToggleCountry}
            onSetScoreBand={filterActions.onSetScoreBand}
            onSetAi={filterActions.onSetAi}
            availableRemotes={availableRemotes}
            availableSeniorities={availableSeniorities}
            availableCompanies={availableCompanies}
            filterRemote={filterRemote}
            filterSeniority={filterSeniority}
            filterCompany={filterCompany}
            filterDateBand={filterDateBand}
            onToggleRemote={filterActions.onToggleRemote}
            onToggleSeniority={filterActions.onToggleSeniority}
            onToggleCompany={filterActions.onToggleCompany}
            onSetDateBand={filterActions.onSetDateBand}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortColumn={onSortColumn}
            onSetSort={filterActions.onSetSort}
            activeFilterCount={activeFilterCount}
            onClearFilters={clearFilters}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onSelectAllVisible={selectAllVisible}
            onClearSelection={clearSelection}
            onBulkUpdateStatus={bulkUpdateStatus}
            onBulkExplain={bulkExplain}
            similarTo={similarTo}
            similarState={similarState}
            similarReason={similarReason}
            onShowSimilar={showSimilarTo}
            onClearSimilar={clearSimilar}
            onOpenCoverLetter={openCoverLetter}
            onOpenTailoredCv={openTailoredCv}
            onOpenDrawer={openDrawer}
            onRequestDismiss={requestDismiss}
            onScan={triggerScan}
            scanning={scanning}
            onOpenManual={() => setShowManualModal(true)}
            showDismissed={showDismissed}
            onToggleDismissed={toggleDismissed}
            dismissedCount={recStats?.byStatus?.dismissed ?? 0}
            showArchived={showArchived}
            onToggleArchived={toggleArchived}
            archivedStats={recStats?.archived ?? null}
            activeRecIndex={activeRecIndex}
            aiKeyReady={!!user?.connectors?.anthropicKey}
            onJumpToRec={(id) => {
              // Reuse the focus-rec event the Command Palette dispatches.
              window.dispatchEvent(new CustomEvent('hunter:focus-rec', { detail: { id } }));
            }}
          />
        )}

        {/* T220 — Archived panel; toggle lives in the Available header. */}
        {showArchived && (
          <ArchivedPanel
            recs={archivedRecs}
            loaded={archivedLoaded}
            stats={recStats?.archived ?? null}
            onRestore={restoreArchived}
            hasMore={archivedHasMore}
            loadingMore={archivedLoadingMore}
            onLoadMore={loadMoreArchived}
            total={archivedTotal}
          />
        )}

        {/* T081 — Dismissed panel; toggle lives in the Available header. */}
        {showDismissed && (
          <DismissedPanel
            recs={dismissedRecs}
            loaded={dismissedLoaded}
            hasMore={dismissedHasMore}
            loadingMore={dismissedLoadingMore}
            onLoadMore={loadMoreDismissed}
            total={dismissedTotal}
            onUndismiss={async (id) => {
              try {
                await api.patch(`/recommendations/${id}`, { status: 'new' });
                // T253/T270 — this was the last `await refresh()`, which threw
                // away every page loaded with Load more to bring back one row.
                // The row rejoins the server's pool, so the cursor moves with
                // it; mergeRows puts it on screen without a round trip.
                // NOT applyStatus: that advances the paging cursor, which is
                // only correct for a row that sorts inside the already-loaded
                // prefix. See the 'restored' case in the reducer.
                const restored = dismissedRecs.find((r) => r.id === id);
                restoreRows(restored ? [{ ...restored, status: 'new', dismissedReason: '' }] : []);
                setRecStats((st) => bumpStatusCount(st, 'dismissed', 'new'));
                refreshNavCounts();
                await loadDismissed();
                toast('Un-dismissed', { kind: 'success' });
              } catch (e) {
                toast(errMsg(e, 'Un-dismiss failed'), { kind: 'error' });
              }
            }}
          />
        )}

        {/* T085 — keyboard hint, next to the list it controls. T256(5): hidden
            below md — there is no keyboard to press it with, and it was eating
            a line of vertical space above the fold on every phone. */}
        <p className="hidden text-xs text-slate-500 md:block">
          <kbd className="rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">j</kbd>/<kbd className="rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">k</kbd> navigate ·
          <kbd className="ml-1 rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">s</kbd> shortlist ·
          <kbd className="ml-1 rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">a</kbd> apply ·
          <kbd className="ml-1 rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">d</kbd> dismiss ·
          <kbd className="ml-1 rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">e</kbd> explain ·
          <kbd className="ml-1 rounded border border-slate-700 bg-slate-900/40 px-1 font-mono">?</kbd> full list
        </p>
      </Zone>

      {/* ── SYSTEM — demoted coverage + source health (T129) ─────────────── */}
      <Zone title="System" tone="muted" hint="Coverage + source health. Quiet unless something needs a look.">
        {!stats ? (
          <StatGridSkeleton count={3} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              title="Jobs in the pool"
              value={stats?.totalJobs ?? '—'}
              hint={statsErr ? 'error' : 'shared corpus, all accounts'}
            />
            <StatCard
              title="New for you in 24h"
              value={stats?.jobsLast24h ?? '—'}
              hint="recommendations created"
              hintFull={
                'Recommendations created for you in the last 24 hours. This used to count new rows in the '
                + 'shared jobs pool, which on a typical day is mostly another account\'s ingestion — it read '
                + '428 on a day your own scans produced 2.'
              }
            />
          </div>
        )}
        {/* T419 — source health had four surfaces, two of them here: a
            "Sources active" stat card and a one-line summary, beside the
            failure banner at the top of this page and the /sources page
            that can actually act on it. Answering "is anything broken?"
            meant reading three and trusting whichever came first. Both
            dashboard widgets are gone; the banner says it when it is true
            and now links to the one place that can do something. */}
      </Zone>

      {/* KeyboardHelpPanel is a fixed overlay — mounted at page level (not in
          a zone) so its positioning isn't clipped by a zone's overflow. */}
      {showKbHelp && (
        <KeyboardHelpPanel
          onClose={() => setShowKbHelp(false)}
          onReplayTour={() => {
            setShowKbHelp(false);
            setTourReplay(true);
          }}
        />
      )}

      {/* T139 — first-run dashboard tour (auto-shows once; replayable via the
          header "Take the tour" button). Mounted at page level so the Modal
          overlay isn't clipped by a zone's overflow; returns null when closed. */}
      <DashboardTour forceOpen={tourReplay} onClose={() => setTourReplay(false)} />

      {/* T417 — the "Signed in as … · role …" footer is gone. The sidebar
          already shows who is signed in on every page, and `role` is the
          same word for all three accounts on this instance, so the line
          repeated one fact and reported a second that cannot vary. */}
    </>
  );
}


// ─── Available jobs ───────────────────────────────────────────────────────────
// Second table — everything not-yet-applied and not-dismissed. Sorted by score
// (the API does this); shortlisted rows get a brand-coloured row so they
// stand out from "new". Reasons + missing skills shown inline so the operator
// can see *why* a row scored well without expanding anything.
// T270(5)/T161 — memoised. The parent ticks a clock every 30s (`nowTick`,
// which only the closing-date chips need) and polls activity and nav counts on
// their own intervals; without this, each of those re-rendered a hundred-row
// table. The twelve filter writers moved to useFilterActions to make it hold —
// a single inline arrow at the call site defeats the whole thing.
const AvailableJobsTable = memo(function AvailableJobsTable({
  recs,
  poolInTargetCities,
  targetCityKeys,
  onFilterTargetCities,
  similarState,
  similarReason,
  totalUnfilteredCount,
  loadedCount,
  totalCount,
  hasMore,
  loadingMore,
  onLoadMore,
  pending,
  explainPending,
  expandedIds,
  onToggleExpand,
  onUpdate,
  onExplain,
  newCount,
  shortlistedCount,
  hasAny,
  searchQ,
  onSearchChange,
  availableSources,
  sourceCounts,
  availableCities,
  cityCounts,
  filterCities,
  onToggleCity,
  availableCountries,
  filterSources,
  filterCountries,
  filterScoreBand,
  filterAi,
  onToggleSource,
  onToggleCountry,
  onSetScoreBand,
  onSetAi,
  availableRemotes,
  availableSeniorities,
  availableCompanies,
  filterRemote,
  filterSeniority,
  filterCompany,
  filterDateBand,
  onToggleRemote,
  onToggleSeniority,
  onToggleCompany,
  onSetDateBand,
  sortKey,
  sortDir,
  onSortColumn,
  onSetSort,
  activeFilterCount,
  onClearFilters,
  selectedIds,
  onToggleSelected,
  onSelectAllVisible,
  onClearSelection,
  onBulkUpdateStatus,
  onBulkExplain,
  similarTo,
  onShowSimilar,
  onClearSimilar,
  onOpenCoverLetter,
  onOpenTailoredCv,
  onOpenDrawer,
  onRequestDismiss,
  onScan,
  scanning,
  onOpenManual,
  showDismissed,
  onToggleDismissed,
  dismissedCount,
  showArchived,
  onToggleArchived,
  archivedStats,
  activeRecIndex,
  aiKeyReady,
  onJumpToRec,
}) {
  const selectedCount = selectedIds.size;
  const visibleSelectedCount = recs.filter((r) => selectedIds.has(r.id)).length;
  const allVisibleSelected = recs.length > 0 && visibleSelectedCount === recs.length;
  return (
    <section id="available-jobs" className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {/* T290b — during a similar-jobs pivot the table is a different list, so
            the queue's counts are not a description of it. Leaving them made the
            header say "showing 1 of 194 · 182 new · 12 shortlisted" over a panel
            holding one row — implying 193 more of the same list below the fold,
            which is exactly the claim the paging footer was hidden for making. */}
        <h2 className="text-xs uppercase tracking-wide text-slate-500">
          {similarTo ? 'Similar jobs' : 'Available jobs'}
          <span className="ml-2 text-slate-600">
            {similarTo
              ? `(${recs.length} ${recs.length === 1 ? 'match' : 'matches'})`
              : `(showing ${recs.length} of ${totalUnfilteredCount} · ${newCount} new · ${shortlistedCount} shortlisted)`}
          </span>
          {/* T454 — where he actually applies, as a number and a filter.
              He chooses 72% of what he sees in his target locations and 15%
              outside; the score separates neither (71% vs 72% inside). This
              says so and gets out of the way — the sort, the score and the
              default view are unchanged. */}
          {!similarTo && poolInTargetCities > 0 && (
            <button
              type="button"
              onClick={onFilterTargetCities}
              className="ml-2 text-xs text-brand-400 underline decoration-dotted transition hover:text-brand-300"
              title={`Show only ${[...targetCityKeys].join(', ')}`}
            >
              {poolInTargetCities} in your target locations
            </button>
          )}
        </h2>
        <div className="flex items-center gap-3">
          {dismissedCount > 0 && (
            <button
              type="button"
              onClick={onToggleDismissed}
              className={
                'text-xs underline decoration-dotted ' +
                (showDismissed ? 'text-brand-400 hover:text-brand-300' : 'text-slate-500 hover:text-slate-300')
              }
              title="Show / hide dismissed recommendations"
            >
              {showDismissed ? 'Hide dismissed' : `Show dismissed (${dismissedCount})`}
            </button>
          )}
          {(archivedStats?.count ?? 0) > 0 && (
            <button
              type="button"
              onClick={onToggleArchived}
              className={
                'text-xs underline decoration-dotted ' +
                (showArchived ? 'text-brand-400 hover:text-brand-300' : 'text-slate-500 hover:text-slate-300')
              }
              title={
                'Rows the current ingestion filter would no longer accept.\n' +
                (archivedStats.bySource ?? [])
                  .slice(0, 6)
                  .map((r) => `${r.source} ${r.count}`)
                  .join('\n')
              }
            >
              {showArchived ? 'Hide archived' : `Show archived (${archivedStats.count})`}
            </button>
          )}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-xs text-amber-300 underline decoration-dotted hover:text-amber-200"
            >
              Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {/* T087 — Conversational triage. Only renders when there are at least
          3 recs to triage AND an AI key is set. */}
      <TriagePanel aiKeyReady={aiKeyReady} recs={recs} onJumpToRec={onJumpToRec} />

      {/* T053 banner, T213 semantics — the source of truth is now the server's
          nearest-neighbour search, so the banner must say which of the three
          empty cases applies. "Nothing similar" and "this job was never
          embedded" look identical to the operator otherwise. */}
      {similarTo && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-600 bg-brand-600/10 p-3 text-sm">
          <span className="text-brand-500">
            ✨ Showing jobs similar to{' '}
            <span className="font-medium">{similarTo.job?.title}</span> @ {similarTo.job?.company}
            <span className="ml-2 text-xs text-slate-400">
              {similarState === 'loading' && 'searching…'}
              {similarState === 'error' && 'semantic search unavailable — try again'}
              {similarState === 'ready' && similarReason === 'not_embedded' &&
                'this job has no embedding yet, so there is nothing to compare against'}
              {similarState === 'ready' && similarReason === 'no_close_neighbours' &&
                'no close matches in the indexed corpus'}
              {similarState === 'ready' && !similarReason && 'ranked by meaning, not shared keywords'}
            </span>
          </span>
          <button
            type="button"
            onClick={onClearSimilar}
            className="ml-auto text-xs text-slate-400 underline decoration-dotted hover:text-slate-200"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* T048 — search + filter bar */}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
        <input
          type="search"
          value={searchQ}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search title / company / skills / city…"
          className="flex-1 min-w-[200px] rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
          aria-label="Search recommendations"
        />
        <FilterDropdown
          label="Source"
          options={availableSources}
          counts={sourceCounts}
          selected={filterSources}
          onToggle={onToggleSource}
        />
        <FilterDropdown
          label="City"
          options={availableCities}
          counts={cityCounts}
          selected={filterCities}
          onToggle={onToggleCity}
        />
        <FilterDropdown
          label="Country"
          options={availableCountries}
          selected={filterCountries}
          onToggle={onToggleCountry}
        />
        {/* T142 — new filter dimensions */}
        <FilterDropdown
          label="Remote"
          options={availableRemotes}
          selected={filterRemote}
          onToggle={onToggleRemote}
        />
        <FilterDropdown
          label="Seniority"
          options={availableSeniorities}
          selected={filterSeniority}
          onToggle={onToggleSeniority}
        />
        <FilterDropdown
          label="Company"
          options={availableCompanies}
          selected={filterCompany}
          onToggle={onToggleCompany}
        />
        <SegmentedControl
          label="Score"
          value={filterScoreBand}
          options={[
            { value: 'all', label: 'all' },
            { value: 'high', label: 'Strong' },
            { value: 'mid', label: 'Fair' },
            { value: 'low', label: 'Weak' },
          ]}
          onChange={onSetScoreBand}
        />
        <SegmentedControl
          label="Added"
          value={filterDateBand}
          options={[
            { value: 'all', label: 'all' },
            { value: '7d', label: '7d' },
            { value: '30d', label: '30d' },
            { value: '90d', label: '90d' },
          ]}
          onChange={onSetDateBand}
        />
        <SegmentedControl
          label="AI"
          value={filterAi}
          options={[
            { value: 'all', label: 'all' },
            { value: 'yes', label: '✨ yes' },
            { value: 'no', label: 'no' },
          ]}
          onChange={onSetAi}
        />
        {/* T142 — sort menu (works on mobile + as a discoverable alt to header clicks) */}
        <SortMenu sortKey={sortKey} sortDir={sortDir} onSetSort={onSetSort} />
      </div>
      {/* T049 — Bulk action bar. Sticky-top when any rows selected. */}
      {selectedCount > 0 && (
        <div className="sticky top-0 z-20 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-600 bg-brand-600/10 px-3 py-2 text-sm shadow-md backdrop-blur">
          <span className="font-medium text-brand-500">{selectedCount} selected</span>
          <span className="text-slate-500">·</span>
          <button
            type="button"
            onClick={() => onBulkUpdateStatus('shortlisted')}
            className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
          >
            Shortlist
          </button>
          <button
            type="button"
            onClick={onBulkExplain}
            className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
          >
            ✨ Explain
          </button>
          <button
            type="button"
            onClick={() => onBulkUpdateStatus('dismissed')}
            className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:border-red-600 hover:text-red-400"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={onClearSelection}
            className="ml-auto text-xs text-slate-500 underline decoration-dotted hover:text-slate-300"
          >
            Clear
          </button>
        </div>
      )}
      {/* T256(4) — while a "similar to X" search is active, `recs` is the
          similarity result, not the queue. The generic states below would then
          announce "No recommendations yet" or offer "Clear filters" for filters
          that are not what emptied the list — flatly contradicting the banner
          three lines up that explains what IS being shown. */}
      {recs.length === 0 && similarTo ? (
        <EmptyState
          icon="◌"
          title="No similar jobs found"
          body={`Nothing else in the queue looks like ${similarTo.job?.title ?? 'this posting'}.`}
          actions={[{ label: 'Back to all recommendations', onClick: onClearSimilar, primary: true }]}
        />
      ) : recs.length === 0 ? (
        hasAny ? (
          activeFilterCount > 0 ? (
            <EmptyState
              icon="◌"
              title="No matches"
              body={`${totalUnfilteredCount} recommendation${totalUnfilteredCount === 1 ? '' : 's'} hidden by your active filters.`}
              actions={[
                { label: 'Clear filters', onClick: onClearFilters },
              ]}
            />
          ) : (
            <EmptyState
              icon="∅"
              title="No available jobs"
              body="Everything is either applied or dismissed. Run a scan to pull fresh postings — or paste a one-off job you spotted."
              actions={[
                { label: scanning ? 'scanning…' : 'Scan now', onClick: onScan, disabled: scanning, primary: true },
                { label: 'Paste a job', onClick: onOpenManual },
              ]}
            />
          )
        ) : (
          <EmptyState
            icon="◌"
            title="No recommendations yet"
            body="Hunter scans your sources daily, but you can kick one off now. If you still get 0 matches, your target roles or locations may be too narrow."
            actions={[
              { label: scanning ? 'scanning…' : 'Scan now', onClick: onScan, disabled: scanning, primary: true },
              { label: 'Edit preferences', to: '/profile' },
            ]}
          />
        )
      ) : (
        <div className="mt-3 hidden overflow-x-auto rounded-xl border border-slate-800 md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <Th className="w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => (allVisibleSelected ? onClearSelection() : onSelectAllVisible())}
                    aria-label={allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
                    className="h-3.5 w-3.5 cursor-pointer rounded border-slate-600 bg-slate-950/40 text-brand-500 focus:ring-1 focus:ring-brand-500"
                  />
                </Th>
                <SortTh label="Title / Why it matches" colKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} />
                <SortTh label="Company" colKey="company" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} />
                <SortTh label="Location" colKey="location" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} />
                <SortTh label="Score" colKey="score" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} className="text-right" />
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {recs.map((r, i) => (
                <AvailableRow
                  key={r.id}
                  rec={r}
                  pending={pending === r.id}
                  explainPending={explainPending === r.id}
                  expanded={expandedIds.has(r.id)}
                  selected={selectedIds.has(r.id)}
                  isActive={i === activeRecIndex}
                  onToggleSelected={() => onToggleSelected(r.id)}
                  onToggleExpand={() => onToggleExpand(r.id)}
                  onUpdate={onUpdate}
                  onExplain={onExplain}
                  onShowSimilar={onShowSimilar}
                  onOpenCoverLetter={onOpenCoverLetter}
                    onOpenTailoredCv={onOpenTailoredCv}
                    onOpenDrawer={onOpenDrawer}
                    onRequestDismiss={onRequestDismiss}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* T047 — Mobile card layout for Available jobs */}
      {recs.length > 0 && (
        <div className="mt-3 space-y-2 md:hidden">
          {recs.map((r, i) => (
            <AvailableCard
              key={r.id}
              rec={r}
              pending={pending === r.id}
              explainPending={explainPending === r.id}
              expanded={expandedIds.has(r.id)}
              selected={selectedIds.has(r.id)}
              isActive={i === activeRecIndex}
              onToggleSelected={() => onToggleSelected(r.id)}
              onToggleExpand={() => onToggleExpand(r.id)}
              onUpdate={onUpdate}
              onExplain={onExplain}
              onShowSimilar={onShowSimilar}
              onOpenCoverLetter={onOpenCoverLetter}
                    onOpenTailoredCv={onOpenTailoredCv}
                    onOpenDrawer={onOpenDrawer}
                    onRequestDismiss={onRequestDismiss}
            />
          ))}
        </div>
      )}
      {/* Paging footer. Without it the operator cannot tell "that is
          everything" from "that is the top 100 by score", and every row below
          the cut-off is invisible — the failure that hid a manually imported
          job scoring 19 when the 100th place sat at 27. */}
      {/* T290 — gated on `hasMore`, the server's own statement, not on
          arithmetic between two counters that drift. `loadedCount` is the fetch
          offset and `totalCount` the feed total; an un-dismiss puts a row back
          into the held set without moving either the way the comparison assumed,
          so the footer reappeared with its button already disabled — an offer
          the operator cannot take.

          And the sentence says LOADED, not "showing". The header above says
          "showing 44 of 182" about the table; this said "Showing 100 of 246"
          about the fetch. Same word, same screen, two populations 64 apart: the
          feed carries applied rows that this table deliberately does not. Each
          sentence is now true about its own subject.

          And it is hidden in similar-jobs mode. That pivot replaces the table
          with a separate, unpaged list, but the footer kept describing the queue
          underneath it — "Loaded 100 of 246 recommendations" over a panel
          showing 7 — and its button fetched the next page of a feed that was not
          on screen. A control that acts on something the operator cannot see is
          worse than no control. */}
      {hasMore && !similarTo && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-400">
          <span>
            Loaded {loadedCount} of {totalCount} recommendations — sorted by score, lowest load last
          </span>
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : `Load ${Math.max(1, Math.min(100, totalCount - loadedCount))} more`}
          </button>
        </div>
      )}
    </section>
  );
})

// T047 — Mobile card variant. Vertical layout, prominent score badge,
// touch-friendly buttons (≥36px). Same logic as AvailableRow but the
// LLM bullets + reasons are always visible (no horizontal-scroll worry).
function AvailableCard({ rec, pending, explainPending, expanded, selected, isActive, onToggleSelected, onToggleExpand, onUpdate, onExplain, onShowSimilar, onOpenCoverLetter, onOpenTailoredCv, onOpenDrawer, onRequestDismiss }) {
  const job = rec.job;
  if (!job) return null;
  const shortlisted = rec.status === 'shortlisted';
  const exp = rec.llmExplanation;
  const fullDesc = job.description ?? '';
  return (
    <div
      // T220 — a DIFFERENT id from the desktop <tr>. Both branches render at
      // once and are switched by CSS alone (`md:block` / `md:hidden`), so a
      // shared id made getElementById return the table row — the hidden one —
      // on mobile, and scroll-into-view silently did nothing there.
      id={`rec-m-${rec.id}`}
      className={
        'rounded-xl border p-3 ' +
        (selected ? 'border-brand-600 bg-brand-600/10' : shortlisted ? 'border-brand-700 bg-brand-600/5' : 'border-slate-800 bg-slate-900/40') +
        (isActive ? ' ring-1 ring-brand-500/60' : '')
      }
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelected}
          aria-label={`Select ${job.title}`}
          className="mt-1 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-950/40 text-brand-500 focus:ring-1 focus:ring-brand-500"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1">
            <button
              type="button"
              onClick={() => onOpenDrawer?.(rec)}
              className="flex-1 text-left font-medium text-slate-100 hover:text-brand-400"
            >
              {job.title}
            </button>
            <a
              href={job.url}
              target="_blank"
              rel="noreferrer"
              title="Open original posting"
              className="shrink-0 rounded px-1.5 py-0.5 text-xs text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
            >
              ↗
            </a>
          </div>
          <div className="mt-0.5 text-xs text-slate-400">
            {job.company}
            {job.location?.city && ` · ${job.location.city}`}
            {job.location?.country && ` · ${job.location.country}`}
          </div>
          <div className="text-xs text-slate-500">
            <code className="font-mono">{job.source}</code>
            {job.location?.remote && job.location.remote !== 'unknown' && ` · ${job.location.remote}`}
            {rec.status !== 'new' && <> <StatusPill compact status={rec.status} /></>}
            {job.firstSeenAt && (
              <span title={formatDateTime(job.firstSeenAt)}> · added {timeAgoFine(job.firstSeenAt)}</span>
            )}
            <ClosingChip date={job.closingDate} />
          </div>
        </div>
        <ScoreBadge
          score={rec.scoreTotal}
          breakdown={rec.scoreBreakdown}
          reasons={rec.reasons}
          risks={rec.risks}
          missingSkills={rec.missingSkills}
        />
      </div>
      <RecInsights rec={rec} dense />
      {expanded && fullDesc && (
        <div className="mt-2 max-h-72 overflow-y-auto rounded border border-slate-800 bg-slate-950/40 p-2 text-xs leading-relaxed text-slate-300 whitespace-pre-wrap">
          {fullDesc}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {fullDesc && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
          >
            {expanded ? '▴ Less' : '▾ Full description'}
          </button>
        )}
        <RecActions
          rec={rec} shortlisted={shortlisted} explained={Boolean(exp)} pending={pending}
          explainPending={explainPending} onUpdate={onUpdate} onExplain={onExplain}
          onRequestDismiss={onRequestDismiss}
        />
        <KebabMenu
          align="left"
          items={[
            {
              label: rec.coverLetter ? '✍ Cover letter ✓ (edit)' : '✍ Generate cover letter',
              onClick: () => onOpenCoverLetter?.(rec),
            },
            {
              label: rec.tailoredCv?.summary ? '📑 Tailored CV ✓ (edit)' : '📑 Tailored CV',
              onClick: () => onOpenTailoredCv?.(rec),
            },
            // T220 — no skills gate. Left over from the client-side "shares >= 3
            // skills" filter that T213 replaced with server-side semantic
            // neighbours, which need no extracted skills at all. 71% of jobs have
            // fewer than 3, so the gate hid the feature on most rows for a reason
            // that had stopped existing.
            { label: 'Show similar jobs', onClick: () => onShowSimilar?.(rec) },
          ]}
        />
      </div>
    </div>
  );
}

function AvailableRow({ rec, pending, explainPending, expanded, selected, isActive, onToggleSelected, onToggleExpand, onUpdate, onExplain, onShowSimilar, onOpenCoverLetter, onOpenTailoredCv, onOpenDrawer, onRequestDismiss }) {
  const job = rec.job;
  if (!job) return null;
  const shortlisted = rec.status === 'shortlisted';
  const exp = rec.llmExplanation;
  // T042 said the collapsed row shows the first ~280 characters. It does not,
  // and never has here: the row renders a ▾ toggle and the full text when
  // expanded, nothing when collapsed. The truncated string was computed on every
  // render of every row and passed to nothing, so it is gone rather than left as
  // a claim the component does not honour.
  const fullDesc = job.description ?? '';
  return (
    <tr
      id={`rec-${rec.id}`}
      className={
        'border-t border-slate-800 align-top hover:bg-slate-900/40 ' +
        (shortlisted ? 'bg-brand-600/5' : '') +
        (selected ? ' bg-brand-600/10' : '') +
        (isActive ? ' ring-1 ring-inset ring-brand-500/60 bg-brand-600/10' : '')
      }
    >
      <Td className="w-8">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelected}
          aria-label={`Select ${job.title}`}
          className="h-3.5 w-3.5 cursor-pointer rounded border-slate-600 bg-slate-950/40 text-brand-500 focus:ring-1 focus:ring-brand-500"
        />
      </Td>
      <Td>
        <div className="flex items-start gap-1">
          <button
            type="button"
            onClick={() => onOpenDrawer?.(rec)}
            className="flex-1 text-left font-medium text-slate-100 hover:text-brand-400"
            title="Open job details"
          >
            {job.title}
          </button>
          <a
            href={job.url}
            target="_blank"
            rel="noreferrer"
            title="Open original posting"
            className="shrink-0 rounded px-1.5 py-0.5 text-xs text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
          >
            ↗
          </a>
          {fullDesc && (
            <button
              type="button"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              className="shrink-0 rounded px-1.5 py-0.5 text-xs text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
              title={expanded ? 'Collapse' : 'Read full description'}
            >
              {expanded ? '▴' : '▾'}
            </button>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
          <code className="font-mono">{job.source}</code>
          {rec.status !== 'new' && <StatusPill compact status={rec.status} />}
          {job.firstSeenAt && (
            <span className="text-[10px] text-slate-500" title={formatDateTime(job.firstSeenAt)}>
              added {timeAgoFine(job.firstSeenAt)}
            </span>
          )}
        </div>
        {/* T042 — expanded full description (whitespace preserved) */}
        {expanded && fullDesc && (
          <div className="mt-2 max-h-96 overflow-y-auto rounded border border-slate-800 bg-slate-950/40 p-3 text-xs leading-relaxed text-slate-300 whitespace-pre-wrap">
            {fullDesc}
            <div className="mt-2 pt-2 border-t border-slate-800 text-xs text-slate-500">
              Source: <a className="underline decoration-dotted hover:text-brand-400" href={job.url} target="_blank" rel="noreferrer">{job.url} ↗</a>
            </div>
          </div>
        )}
        <RecInsights rec={rec} />
      </Td>
      <Td className="text-slate-300">{job.company}</Td>
      <Td>
        <LocationCell location={job.location} />
      </Td>
      <Td className="text-right">
        <ScoreBadge
          score={rec.scoreTotal}
          breakdown={rec.scoreBreakdown}
          reasons={rec.reasons}
          risks={rec.risks}
          missingSkills={rec.missingSkills}
        />
      </Td>
      <Td className="text-right">
        <div className="flex flex-wrap justify-end gap-1.5">
          {/* T084 — Primary actions only. Lesser-used actions live in the
              kebab menu so the row stays scannable. */}
          {/* T256(1) — these read title="s" / "a" / "d" once: a bare keystroke
              is not a tooltip, it is a riddle. T270(4)/T161 — and they lived
              here twice, so the fix reached desktop only. One component now. */}
          <RecActions
            rec={rec} shortlisted={shortlisted} explained={Boolean(exp)} pending={pending}
            explainPending={explainPending} onUpdate={onUpdate} onExplain={onExplain}
            onRequestDismiss={onRequestDismiss}
          />
          <KebabMenu
            items={[
              {
                label: rec.coverLetter ? '✍ Cover letter ✓ (edit)' : '✍ Generate cover letter',
                onClick: () => onOpenCoverLetter?.(rec),
                kbd: 'c',
              },
              {
                label: rec.tailoredCv?.summary ? '📑 Tailored CV ✓ (edit)' : '📑 Tailored CV',
                onClick: () => onOpenTailoredCv?.(rec),
                kbd: 't',
              },
              // T220 — no skills gate. Left over from the client-side "shares >= 3
              // skills" filter that T213 replaced with server-side semantic
              // neighbours, which need no extracted skills at all. 71% of jobs have
              // fewer than 3, so the gate hid the feature on most rows for a reason
              // that had stopped existing.
              { label: 'Show similar jobs', onClick: () => onShowSimilar?.(rec) },
            ]}
          />
        </div>
      </Td>
    </tr>
  );
}

// ─── Shared cell helpers ──────────────────────────────────────────────────────
// T052 — First-day onboarding checklist. Shows when any of the load-bearing
// setup steps haven't been completed. Auto-hides once all three are done.
// Sits between the scan result panel and the stats grid so it's the most
// prominent thing on a fresh dashboard.
function OnboardingBanner({ user, stats }) {
  const hasAnthropic = !!user?.connectors?.anthropicKey;
  const hasTargetRoles = (user?.preferences?.targetRoles ?? []).length > 0;
  const hasScanned = !!stats?.lastScan;
  // Don't render until we know stats (otherwise it'd flash on every reload).
  if (!stats) return null;
  if (hasAnthropic && hasTargetRoles && hasScanned) return null;
  const steps = [
    {
      done: hasAnthropic,
      title: 'Add your Anthropic API key',
      hint: 'Unlocks AI explanation bullets (~$0.011/rec, ~$1.50/month).',
      cta: 'Open Settings',
      href: '/settings',
    },
    {
      done: hasTargetRoles,
      title: 'Tell hunter what you want',
      hint: 'Target roles + locations + seniority drive the daily scanner.',
      cta: 'Open Profile',
      href: '/profile',
    },
    {
      done: hasScanned,
      title: 'Run your first scan',
      hint: 'Pulls jobs from every configured board and scores them against your profile.',
      cta: 'Scan now',
      // Special — no link, the scan button is right at the top of this page.
      href: null,
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return (
    <section className="mt-6 rounded-xl border border-amber-700 bg-amber-950/20 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-medium text-amber-300">
          ✨ Get started — {doneCount}/3 done
        </h2>
        <span className="text-xs text-amber-400/70">setup checklist</span>
      </div>
      <ol className="mt-3 space-y-2 text-sm">
        {steps.map((s, i) => (
          <li
            key={i}
            className={
              'flex items-center gap-3 rounded-lg border p-2.5 ' +
              (s.done
                ? 'border-emerald-800 bg-emerald-950/20 text-emerald-300'
                : 'border-slate-800 bg-slate-900/30 text-slate-200')
            }
          >
            <span
              className={
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ' +
                (s.done ? 'bg-emerald-600/20 text-emerald-400' : 'bg-amber-600/20 text-amber-300')
              }
            >
              {s.done ? '✓' : i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{s.title}</div>
              <div className="text-xs text-slate-500">{s.hint}</div>
            </div>
            {!s.done &&
              (s.href ? (
                <Link
                  to={s.href}
                  className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500"
                >
                  {s.cta}
                </Link>
              ) : (
                <span className="shrink-0 text-xs text-slate-500">↑ button up top</span>
              ))}
          </li>
        ))}
      </ol>
    </section>
  );
}

// T048 — Multi-select dropdown for source/country filter chips. Click
// label to toggle a popover; each option toggles on click. Selected count
// shown on the trigger.


// T048 — Segmented control for score band + AI status (single-select).


// T220 — takes a `title`. The keyboard shortcuts existed and were invisible:
// nothing on the row said that `s` shortlists, so they were discoverable only
// by opening the `?` panel, which most people never do.

